/**
 * OpenAI, and everything that speaks its dialect.
 *
 * The Chat Completions shape has become the lingua franca: Groq, Together,
 * OpenRouter, Ollama, LM Studio and vLLM all answer it. So this one adapter is
 * really several — set `baseUrl` to `http://localhost:11434/v1` and the family's
 * assistant is a model on their own machine, with no key and nothing leaving the
 * house. That is a genuinely good option for a family archive, and it costs
 * nothing extra to support because the wire format is the same.
 */
import type { AskReply, AskRequest, AssistantProvider, ToolCall, ToolDef, Turn } from './types';
import { httpError, networkError } from './types';

const API = 'https://api.openai.com/v1';

interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content?: string | null;
  tool_calls?: Array<{ id: string; type: 'function'; function: { name: string; arguments: string } }>;
  tool_call_id?: string;
}

function toMessages(system: string, turns: Turn[]): ChatMessage[] {
  const out: ChatMessage[] = [{ role: 'system', content: system }];
  for (const turn of turns) {
    // Each tool result is its own message here, not part of the user's turn.
    if (turn.results?.length) {
      for (const r of turn.results) out.push({ role: 'tool', tool_call_id: r.id, content: r.output });
    }
    if (turn.role === 'assistant' && turn.calls?.length) {
      out.push({
        role: 'assistant',
        content: turn.text || null,
        tool_calls: turn.calls.map((c) => ({ id: c.id, type: 'function' as const, function: { name: c.name, arguments: JSON.stringify(c.input) } })),
      });
      continue;
    }
    if (turn.text) out.push({ role: turn.role, content: turn.text });
  }
  return out;
}

function toTools(tools: ToolDef[]) {
  return tools.map((t) => ({ type: 'function' as const, function: { name: t.name, description: t.description, parameters: t.schema } }));
}

export function createOpenAiProvider(opts: { apiKey: string; model: string; baseUrl?: string }): AssistantProvider {
  const base = (opts.baseUrl?.trim() || API).replace(/\/+$/, '');
  // A local model needs no key; a hosted one does. Treat "has a base url" as "may be local".
  const needsKey = !opts.baseUrl?.trim();
  return {
    id: 'openai',
    label: 'OpenAI-compatible',
    ready: () => !needsKey || !!opts.apiKey.trim(),
    notReadyKey: () => (!needsKey || opts.apiKey.trim() ? null : 'assistant.error.noKey'),
    async ask({ system, turns, tools, signal }: AskRequest): Promise<AskReply> {
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (opts.apiKey.trim()) headers.authorization = `Bearer ${opts.apiKey.trim()}`;
      let res: Response;
      try {
        res = await fetch(`${base}/chat/completions`, {
          method: 'POST',
          signal,
          headers,
          body: JSON.stringify({
            model: opts.model,
            max_tokens: 700,
            messages: toMessages(system, turns),
            tools: toTools(tools),
          }),
        });
      } catch (e) {
        throw networkError(e);
      }
      if (!res.ok) throw await httpError(res);

      const data = (await res.json()) as { choices?: Array<{ message?: ChatMessage }> };
      const msg = data.choices?.[0]?.message;
      const calls: ToolCall[] = [];
      for (const c of msg?.tool_calls ?? []) {
        let input: Record<string, unknown> = {};
        try {
          input = JSON.parse(c.function.arguments || '{}') as Record<string, unknown>;
        } catch {
          // A model that emits malformed JSON should not take the panel down;
          // the empty input makes the tool fail cleanly and say why.
        }
        calls.push({ id: c.id, name: c.function.name, input });
      }
      return { text: (msg?.content ?? '').trim(), calls };
    },
  };
}
